import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Edit, AlertCircle, Grape } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useMenu } from "../hooks/useMenu";
import MenuEditModal from "./MenuEditModal";
import { toast } from "../hooks/use-toast";

const MenuSection = () => {
  const { isAuthenticated } = useAuth();
  const { menu, loading, error, updateMenu } = useMenu();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleSaveMenu = async (updatedMenu) => {
    const result = await updateMenu(updatedMenu);
    if (result.success) {
      toast({
        title: "Menu mis à jour",
        description: "Le menu a été sauvegardé avec succès.",
      });
    } else {
      toast({
        title: "Erreur",
        description: result.error,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <section id="menu" className="section-padding bg-warm-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
            <div className="h-96 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="menu" className="section-padding bg-warm-white">
        <div className="max-w-4xl mx-auto">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </section>
    );
  }

  if (!menu) {
    return null;
  }

  return (
    <section id="menu" className="section-padding bg-warm-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <h2 className="text-4xl font-serif font-bold text-dark">
              MENU {menu.season}
            </h2>
            {isAuthenticated && (
              <Button
                onClick={() => setIsEditModalOpen(true)}
                variant="outline"
                size="sm"
                className="ml-4 border-primary text-dark hover:bg-primary hover:text-white"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
            )}
          </div>
        </div>

        <Card className="border-2 border-gray-100 shadow-lg">
          <CardContent className="p-12">
            <div className="text-center space-y-12">
              {/* Amuse-bouche */}
              <div>
                <h3 className="text-xl font-serif font-semibold text-dark mb-2">
                  {menu.amuse_bouche}
                </h3>
              </div>

              <div className="divider"></div>

              {/* Entrée */}
              <div>
                <h3 className="text-2xl font-serif font-semibold text-dark mb-6">
                  Entrée
                </h3>
                <div className="space-y-4">
                  <h4 className="text-xl font-semibold text-gray-800">
                    {menu.appetizer.name}
                  </h4>
                  <p className="text-xl font-semibold text-gray-800">
                    {menu.appetizer.description}
                  </p>
                  <div className="flex items-center justify-center text-gray-600">
                    <Grape
                      className="h-5 w-5 mr-3"
                      style={{ color: "#f0d188ff" }}
                    />
                    <span className="text-base leading-none">
                      {menu.appetizer.wine}
                    </span>
                  </div>
                </div>
              </div>

              <div className="divider"></div>

              {/* Plat */}
              <div>
                <h3 className="text-2xl font-serif font-semibold text-dark mb-6">
                  Plat
                </h3>
                <div className="space-y-4">
                  <h4 className="text-xl font-semibold text-gray-800">
                    {menu.main.name}
                  </h4>
                  <p className="text-xl font-semibold text-gray-800">
                    {menu.main.description}
                  </p>
                  <div className="flex items-center justify-center text-gray-600">
                    <Grape
                      className="h-5 w-5 mr-3"
                      style={{ color: "#800020" }}
                    />
                    <span className="text-base leading-none">
                      {menu.main.wine}
                    </span>
                  </div>

                  <div className="flex justify-center"></div>
                </div>
              </div>

              <div className="divider"></div>

              {/* Dessert */}
              <div>
                <h3 className="text-2xl font-serif font-semibold text-dark mb-6">
                  Dessert
                </h3>
                <div className="space-y-4">
                  <h4 className="text-xl font-semibold text-gray-800">
                    {menu.dessert.name}
                  </h4>
                  <p className="text-gray-600 leading-relaxed">
                    {menu.dessert.description}
                  </p>
                </div>
              </div>

              <div className="divider"></div>

              {/* Price */}
              <div className="text-center">
                <p className="text-gray-600 mb-2">{menu.note}</p>
                <div className="text-3xl font-serif font-bold text-dark">
                  {menu.price}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-8">
          <p className="text-gray-600">
            Menu de saison avec des produits frais et locaux
          </p>
        </div>
      </div>

      {/* Edit Modal */}
      <MenuEditModal
        menu={menu}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveMenu}
      />
    </section>
  );
};

export default MenuSection;
